import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { navLinks } from '../config/navigation';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabaseClient';
import { sanitizeSearchQuery } from '../utils/sanitizeSearchQuery';
import RatingModal from './RatingModal';
import './Navbar.css';

/* ─────────────────────────────────────────────────────────────
   PERSON ICON — fallback for avatar button when no initial
   ───────────────────────────────────────────────────────────── */
function PersonIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  );
}

/* ─────────────────────────────────────────────────────────────
   NAVBAR — top navigation bar present on every page.

   Contains:
   - Logo + nav links
   - Global player search (works for everyone)
   - Notification bell (logged-in users only)
   - Theme toggle + auth buttons
   ───────────────────────────────────────────────────────────── */

function SunIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5"/>
      <line x1="12" y1="1" x2="12" y2="3"/>
      <line x1="12" y1="21" x2="12" y2="23"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
      <line x1="1" y1="12" x2="3" y2="12"/>
      <line x1="21" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  );
}

function FlowerIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {/* 5 petals radiating from center */}
      <ellipse cx="12" cy="5.5" rx="2.2" ry="3.5"/>
      <ellipse cx="12" cy="5.5" rx="2.2" ry="3.5" transform="rotate(72 12 12)"/>
      <ellipse cx="12" cy="5.5" rx="2.2" ry="3.5" transform="rotate(144 12 12)"/>
      <ellipse cx="12" cy="5.5" rx="2.2" ry="3.5" transform="rotate(216 12 12)"/>
      <ellipse cx="12" cy="5.5" rx="2.2" ry="3.5" transform="rotate(288 12 12)"/>
      {/* Center circle */}
      <circle cx="12" cy="12" r="2.2" fill="currentColor" stroke="none"/>
    </svg>
  );
}

function PlanetIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {/* Back arc — the half of the ring that goes behind the planet */}
      <path d="M 20.23 7.25 A 9.5 3 -30 0 0 3.77 16.75"/>
      {/* Planet fill — covers the back arc where it overlaps the planet */}
      <circle cx="12" cy="12" r="4.5" style={{fill: 'var(--bg-surface)'}}/>
      {/* Front arc — drawn on top of the planet */}
      <path d="M 20.23 7.25 A 9.5 3 -30 0 1 3.77 16.75"/>
      {/* Planet stroke — redrawn on top so it's crisp */}
      <circle cx="12" cy="12" r="4.5" fill="none"/>
    </svg>
  );
}

function TerminalIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="4 17 10 11 4 5"/>
      <line x1="12" y1="19" x2="20" y2="19"/>
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/>
      <line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"/>
      <line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  );
}

function TrophyIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9H3V4h3"/>
      <path d="M18 9h3V4h-3"/>
      <path d="M6 4h12v7a6 6 0 0 1-12 0V4z"/>
      <line x1="12" y1="17" x2="12" y2="21"/>
      <line x1="8" y1="21" x2="16" y2="21"/>
    </svg>
  );
}

function BellIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
      <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
    </svg>
  );
}

function Navbar({ theme, toggleTheme }) {
  const [menuOpen, setMenuOpen]         = useState(false);
  const { user, signOut }               = useAuth();
  const navigate                        = useNavigate();

  // ── Player search ─────────────────────────────────────────
  const [searchQuery,   setSearchQuery]   = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen,    setSearchOpen]    = useState(false);
  const searchRef = useRef(null);

  // ── Notifications ─────────────────────────────────────────
  const [notifications, setNotifications] = useState([]);
  const [bellOpen,      setBellOpen]      = useState(false);
  const bellRef = useRef(null);

  // ── User menu (avatar dropdown) ───────────────────────────
  const [userMenuOpen,  setUserMenuOpen]  = useState(false);
  const [profileUsername, setProfileUsername] = useState('');
  const [profileAvatarUrl, setProfileAvatarUrl] = useState('');
  const userMenuRef = useRef(null);

  // ── Placement gate ────────────────────────────────────────
  const [isRated,           setIsRated]           = useState(true);
  const [ratingModalOpen,   setRatingModalOpen]   = useState(false);
  const [pendingMatchNotif, setPendingMatchNotif] = useState(null);

  const closeMenu = () => setMenuOpen(false);

  // Search: query profiles as user types (debounced 200ms)
  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      const { data } = await supabase
        .from('profiles')
        .select('id, username, elo_rating')
        .or(`full_name.ilike.%${sanitizeSearchQuery(searchQuery.trim())}%,username.ilike.%${sanitizeSearchQuery(searchQuery.trim())}%`)
        .limit(6);
      setSearchResults(data || []);
    }, 200);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Notifications: fetch on login + subscribe to real-time inserts
  useEffect(() => {
    if (!user) { setNotifications([]); return; }

    fetchNotifications();

    // Real-time subscription removed — Supabase real-time publication
    // has zero tables enrolled, so postgres_changes did nothing.
    // UI refresh is handled via window custom events instead.
    window.addEventListener('ranktt:friendResolved',      fetchNotifications);
    window.addEventListener('ranktt:notificationsRead',   fetchNotifications);

    return () => {
      window.removeEventListener('ranktt:friendResolved',    fetchNotifications);
      window.removeEventListener('ranktt:notificationsRead', fetchNotifications);
    };
  }, [user]);

  async function fetchNotifications() {
    // Show only actionable notifications that haven't been acted on yet.
    // Using actioned IS NULL (not is_read) so that visiting the notifications
    // page (which marks is_read) doesn't wipe pending items from the bell.
    const { data: notifs } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user.id)
      .in('type', ['friend_request', 'match_confirmation', 'tournament_completed'])
      .is('actioned', null)
      .order('created_at', { ascending: false });

    const notifData = notifs || [];

    const relatedIds = [...new Set(notifData.map(n => n.related_user_id).filter(Boolean))];
    let usernameMap = {};
    if (relatedIds.length) {
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, username')
        .in('id', relatedIds);
      (profiles || []).forEach(p => { usernameMap[p.id] = p.username; });
    }

    setNotifications(notifData.map(n => ({
      ...n,
      related_username: usernameMap[n.related_user_id] || null,
    })));
  }

  async function handleRespond(notif, accept) {
    // Optimistic: remove from bell immediately so buttons vanish at once
    setNotifications(prev => prev.filter(n => n.id !== notif.id));
    await supabase.rpc('respond_to_friend_request', {
      p_friendship_id: notif.related_id,
      p_accept:        accept,
    });
    window.dispatchEvent(new CustomEvent('ranktt:friendResolved'));
  }

  async function handleMatchRespond(notif, accept) {
    if (accept && !isRated) {
      setPendingMatchNotif(notif);
      setRatingModalOpen(true);
      return;
    }
    // Optimistic: remove from bell immediately so buttons vanish at once
    setNotifications(prev => prev.filter(n => n.id !== notif.id));
    const { error } = await supabase.rpc('confirm_match', {
      p_match_id: notif.related_id,
      p_accept:   accept,
    });
    if (error) {
      // Restore the notification if the RPC failed
      fetchNotifications();
      alert('Error: ' + error.message);
      return;
    }
    setBellOpen(false);
    window.dispatchEvent(new CustomEvent('ranktt:matchResolved'));
  }

  async function handleTournamentRespond(notif, confirmed) {
    // Optimistic: remove from bell immediately
    setNotifications(prev => prev.filter(n => n.id !== notif.id));
    await supabase
      .from('notifications')
      .update({ actioned: confirmed ? 'confirmed' : 'declined' })
      .eq('id', notif.id);
  }

  // Fetch username + avatar_url for the avatar button
  useEffect(() => {
    if (!user) { setProfileUsername(''); setProfileAvatarUrl(''); return; }
    supabase
      .from('profiles')
      .select('username, avatar_url, is_rated')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        if (data?.username)   setProfileUsername(data.username);
        if (data?.avatar_url) setProfileAvatarUrl(data.avatar_url);
        setIsRated(data?.is_rated ?? false);
      });

    // Refresh avatar and rating status when the user saves profile changes
    function onProfileUpdated() {
      supabase
        .from('profiles')
        .select('username, avatar_url, is_rated')
        .eq('id', user.id)
        .single()
        .then(({ data }) => {
          if (data?.username)   setProfileUsername(data.username);
          setProfileAvatarUrl(data?.avatar_url || '');
          setIsRated(data?.is_rated ?? false);
        });
    }
    window.addEventListener('ranktt:profileUpdated', onProfileUpdated);
    return () => window.removeEventListener('ranktt:profileUpdated', onProfileUpdated);
  }, [user]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
      if (bellRef.current && !bellRef.current.contains(e.target)) {
        setBellOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleSelectPlayer(username) {
    setSearchQuery('');
    setSearchResults([]);
    setSearchOpen(false);
    closeMenu();
    navigate(`/profile/${username}`);
  }

  const hasNotifications   = notifications.length > 0;
  const hasFriendRequests  = notifications.some(n => n.type === 'friend_request');

  return (
    <>
    <nav className={`navbar${user ? ' navbar--authed' : ''}`}>
      <div className="navbar-inner">

        <Link to={user ? '/dashboard' : '/'} className="navbar-logo">
          Rank<span>TT</span>
        </Link>

        {/* Nav links + mobile extras */}
        <div className={`navbar-links ${menuOpen ? 'open' : ''}`}>
          {navLinks
            .filter(link => !link.authRequired || user)
            .filter(link => !link.hideWhenAuthed || !user)
            .map((link) => {
              // Resolve icon: 'trophy' → <TrophyIcon />, any other string → render as text
              const iconNode = link.icon === 'trophy' ? <TrophyIcon /> : link.icon;

              const inner = link.icon ? (
                <span className="nav-collapsible">
                  <span className="nav-label">{link.label}</span>
                  <span className="nav-icon">{iconNode}</span>
                </span>
              ) : link.label;

              const cls = link.icon ? 'nav-has-icon' : undefined;

              return link.isRoute ? (
                <Link key={link.label} to={link.href} onClick={closeMenu} className={cls}>{inner}</Link>
              ) : (
                <a key={link.label} href={link.href} onClick={closeMenu} className={cls}>{inner}</a>
              );
            })}

          {/* Search inside mobile menu */}
          <div className="navbar-search-mobile" ref={null}>
            <div className="navbar-search-wrap">
              <SearchIcon />
              <input
                className="navbar-search-input"
                placeholder="Search players..."
                value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); setSearchOpen(true); }}
              />
            </div>
            {searchOpen && searchResults.length > 0 && (
              <div className="navbar-search-dropdown">
                {searchResults.map(p => (
                  <button key={p.id} className="navbar-search-result"
                    onClick={() => handleSelectPlayer(p.username)}>
                    <span className="nsr-name">{p.username}</span>
                    <span className="nsr-elo">{p.elo_rating}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Auth buttons — mobile menu only */}
          <div className="navbar-mobile-auth">
            {user ? (
              <>
                <Link to="/tournaments" className="btn-ghost" onClick={closeMenu}>Tournaments</Link>
                <Link to="/friends" className="btn-ghost" onClick={closeMenu}>Friends</Link>
                <Link to="/settings" className="btn-ghost" onClick={closeMenu}>Settings</Link>
                <button className="btn-logout" onClick={() => { signOut(); closeMenu(); }}>Log out</button>
              </>
            ) : (
              <>
                <Link to="/login"  className="btn-ghost"   onClick={closeMenu}>Log in</Link>
                <Link to="/signup" className="btn-primary" onClick={closeMenu}>Sign up free</Link>
              </>
            )}
          </div>
        </div>

        {/* Right side — desktop */}
        <div className="navbar-right">

          {/* Report Match — logged-in users only */}
          {user && (
            <button
              className="navbar-report-btn"
              onClick={() => window.dispatchEvent(new CustomEvent('ranktt:openReportMatch'))}
              aria-label="Report a match"
            >
              <span className="navbar-report-icon"><PlusIcon /></span>
              <span className="navbar-report-label">Report Match</span>
            </button>
          )}

          {/* Global player search */}
          <div className="navbar-search" ref={searchRef}>
            <div className="navbar-search-wrap">
              <label htmlFor="navbar-search-input" className="navbar-search-icon">
                <SearchIcon />
              </label>
              <input
                id="navbar-search-input"
                className="navbar-search-input"
                placeholder="Search players..."
                value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); setSearchOpen(true); }}
                onFocus={() => setSearchOpen(true)}
              />
            </div>
            {searchOpen && searchResults.length > 0 && (
              <div className="navbar-search-dropdown">
                {searchResults.map(p => (
                  <button key={p.id} className="navbar-search-result"
                    onClick={() => handleSelectPlayer(p.username)}>
                    <span className="nsr-name">{p.username}</span>
                    <span className="nsr-elo">{p.elo_rating}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Notification bell — logged-in users only */}
          {user && (
            <div className="navbar-bell" ref={bellRef}>
              <button
                className="navbar-bell-btn"
                onClick={() => setBellOpen(!bellOpen)}
                aria-label="Notifications"
              >
                <BellIcon />
                {hasNotifications && <span className="navbar-bell-dot" />}
              </button>

              {bellOpen && (
                <div className="navbar-bell-dropdown">
                  <p className="navbar-bell-title">Notifications</p>
                  {notifications.length === 0 ? (
                    <p className="navbar-bell-empty">You're all caught up!</p>
                  ) : (
                    notifications.map(notif => (
                      <div key={notif.id} className="navbar-bell-item">
                        <p className="navbar-bell-content">{notif.content}</p>
                        {notif.type === 'friend_request' && (
                          <>
                            <Link
                              to={`/profile/${notif.related_username}`}
                              className="navbar-bell-profile-link"
                              onClick={() => setBellOpen(false)}
                            >
                              View profile →
                            </Link>
                            <div className="navbar-bell-actions">
                              <button className="nba-accept"
                                onClick={() => handleRespond(notif, true)}>
                                Accept
                              </button>
                              <button className="nba-decline"
                                onClick={() => handleRespond(notif, false)}>
                                Decline
                              </button>
                            </div>
                          </>
                        )}
                        {notif.type === 'match_confirmation' && (
                          <div className="navbar-bell-actions">
                            <button className="nba-accept"
                              onClick={() => handleMatchRespond(notif, true)}>
                              Confirm
                            </button>
                            <button className="nba-decline"
                              onClick={() => handleMatchRespond(notif, false)}>
                              Decline
                            </button>
                          </div>
                        )}
                        {notif.type === 'tournament_completed' && (
                          <>
                            <Link
                              to={`/tournaments/${notif.related_id}`}
                              className="navbar-bell-profile-link"
                              onClick={() => setBellOpen(false)}
                            >
                              View tournament →
                            </Link>
                            <div className="navbar-bell-actions">
                              <button className="nba-accept"
                                onClick={() => handleTournamentRespond(notif, true)}>
                                Confirm
                              </button>
                              <button className="nba-decline"
                                onClick={() => handleTournamentRespond(notif, false)}>
                                Decline
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))
                  )}
                  <Link
                    to="/notifications"
                    className="navbar-bell-footer"
                    onClick={() => setBellOpen(false)}
                  >
                    See all notifications →
                  </Link>
                </div>
              )}
            </div>
          )}

          <button
            className="theme-toggle"
            onClick={toggleTheme}
            aria-label={
              theme === 'hacker'   ? 'Exit hacker mode'        :
              theme === 'dark'     ? 'Switch to midnight mode' :
              theme === 'midnight' ? 'Switch to flower mode'   :
              theme === 'flower'   ? 'Switch to light mode'    :
                                     'Switch to dark mode'
            }
          >
            {theme === 'hacker'   ? <TerminalIcon /> :
             theme === 'dark'     ? <MoonIcon />     :
             theme === 'midnight' ? <PlanetIcon />   :
             theme === 'flower'   ? <FlowerIcon />   :
                                    <SunIcon />}
          </button>

          {user ? (
            <div className="navbar-user-menu" ref={userMenuRef}>
              <button
                className="navbar-avatar-btn"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                aria-label="Account menu"
              >
                {profileAvatarUrl ? (
                  <img
                    src={profileAvatarUrl}
                    alt={profileUsername}
                    className="navbar-avatar-img"
                  />
                ) : profileUsername ? (
                  profileUsername[0].toUpperCase()
                ) : (
                  <PersonIcon />
                )}
                {hasFriendRequests && <span className="navbar-avatar-badge" />}
              </button>

              {userMenuOpen && (
                <div className="navbar-user-dropdown">
                  <Link
                    to={profileUsername ? `/profile/${profileUsername}` : '/dashboard'}
                    className="navbar-user-dropdown-item"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    My Profile
                  </Link>
                  <Link
                    to="/notifications"
                    className="navbar-user-dropdown-item"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    Notifications
                  </Link>
                  <Link
                    to="/statistics"
                    className="navbar-user-dropdown-item"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    Statistics
                  </Link>
                  <Link
                    to="/tournaments"
                    className="navbar-user-dropdown-item"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    Tournaments
                  </Link>
                  <Link
                    to="/clubs"
                    className="navbar-user-dropdown-item"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    Clubs
                  </Link>
                  <Link
                    to="/friends"
                    className="navbar-user-dropdown-item"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    Friends
                    {hasFriendRequests && <span className="navbar-dropdown-badge" />}
                  </Link>
                  <Link
                    to="/settings"
                    className="navbar-user-dropdown-item"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    Settings
                  </Link>
                  <button
                    className="navbar-user-dropdown-item navbar-user-dropdown-logout"
                    onClick={() => { signOut(); setUserMenuOpen(false); }}
                  >
                    Log out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link to="/login"  className="btn-ghost">Log in</Link>
              <Link to="/signup" className="btn-primary">Sign up free</Link>
            </>
          )}
        </div>

        {/* Hamburger — mobile only */}
        <button
          className={`hamburger ${menuOpen ? 'open' : ''}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle navigation menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

      </div>

    </nav>

      {/* Placement gate — portalled to document.body so it escapes the
          navbar's fixed/stacking context and renders above all page content */}
      {ratingModalOpen && createPortal(
        <RatingModal
          isOpen={true}
          onClose={() => {
            setRatingModalOpen(false);
            setPendingMatchNotif(null);
          }}
          userId={user?.id}
          onRated={() => {
            setIsRated(true);
            setRatingModalOpen(false);
            if (pendingMatchNotif) {
              handleMatchRespond(pendingMatchNotif, true);
            }
            setPendingMatchNotif(null);
          }}
        />,
        document.body
      )}
    </>
  );
}

export default Navbar;
